// Doctor revenue share (docs/plans/phase3-accounting-and-revenue-share.md §1.1, R1–R7).
// Pure: the API finds the charges a practitioner provided and the events that earn or
// reverse a share; these functions pick the rule and compute the share in integer cents.
//
// Rounding (R3): every event's share is rounded half away from zero once (ADR 0006,
// `applyRate` and `scaleMoney`). Because that rounding is symmetric, reversing a whole
// charge gives exactly minus what it earned.

import type { ServiceItemCategory } from '../catalogue.ts';
import { allocateMoney, applyRate, moneyFromCents, scaleMoney } from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { CalendarDay } from '../time.ts';
import { compareDays } from '../time.ts';

export const REVENUE_SHARE_BASES = ['billed', 'collected'] as const;
export type RevenueShareBasis = (typeof REVENUE_SHARE_BASES)[number];

export const REVENUE_SHARE_BASIS_WORDS: Record<RevenueShareBasis, string> = {
  billed: 'Billed revenue',
  collected: 'Collected revenue',
};

export const SHARE_METHODS = ['percent', 'fixed'] as const;
export type ShareMethod = (typeof SHARE_METHODS)[number];

export const SHARE_EVENT_KINDS = [
  'billed',
  'collected',
  'waiver',
  'discount',
  'credit_note',
  'refund',
  'cancellation',
] as const;
export type ShareEventKind = (typeof SHARE_EVENT_KINDS)[number];

export const SHARE_EVENT_WORDS: Record<ShareEventKind, string> = {
  billed: 'Billed',
  collected: 'Collected',
  waiver: 'Waiver',
  discount: 'Discount',
  credit_note: 'Credit note',
  refund: 'Refund',
  cancellation: 'Charge cancelled',
};

/** Events that take a share back. */
export function isReversal(kind: ShareEventKind): boolean {
  return kind !== 'billed' && kind !== 'collected';
}

export interface ShareRule {
  readonly id: string;
  /** Exactly one of the item and the category is set. */
  readonly serviceItemId: string | null;
  readonly category: ServiceItemCategory | null;
  readonly method: ShareMethod;
  /** Percent rules: hundredths of a percent (12.5% is 1250). */
  readonly rateBasisPoints: number | null;
  /** Fixed rules: cents per unit of the charge's quantity (R2). */
  readonly fixedCents: bigint | null;
  readonly effectiveFrom: CalendarDay;
  /** Exclusive; null while open-ended. */
  readonly effectiveTo: CalendarDay | null;
}

export function ruleIsEffectiveOn(
  rule: Pick<ShareRule, 'effectiveFrom' | 'effectiveTo'>,
  day: CalendarDay,
) {
  return (
    compareDays(rule.effectiveFrom, day) <= 0 &&
    (rule.effectiveTo === null || compareDays(day, rule.effectiveTo) < 0)
  );
}

/** Two rules of the same scope whose dates overlap; the API refuses the second. */
export function rulesOverlap(
  a: Pick<ShareRule, 'serviceItemId' | 'category' | 'effectiveFrom' | 'effectiveTo'>,
  b: Pick<ShareRule, 'serviceItemId' | 'category' | 'effectiveFrom' | 'effectiveTo'>,
): boolean {
  if (a.serviceItemId !== b.serviceItemId || a.category !== b.category) return false;
  const aEndsAfterBStarts =
    a.effectiveTo === null || compareDays(b.effectiveFrom, a.effectiveTo) < 0;
  const bEndsAfterAStarts =
    b.effectiveTo === null || compareDays(a.effectiveFrom, b.effectiveTo) < 0;
  return aEndsAfterBStarts && bEndsAfterAStarts;
}

/** The rule for a charge on its service day: a rule for the item wins over its category's. */
export function ruleFor(
  rules: readonly ShareRule[],
  charge: { serviceItemId: string; category: ServiceItemCategory; serviceDay: CalendarDay },
): ShareRule | null {
  const effective = rules.filter((r) => ruleIsEffectiveOn(r, charge.serviceDay));
  return (
    effective.find((r) => r.serviceItemId === charge.serviceItemId) ??
    effective.find((r) => r.serviceItemId === null && r.category === charge.category) ??
    null
  );
}

/**
 * The share of one event. `baseCents` is the amount of the charge the event concerns: the
 * whole total when billed, what was collected, or what a waiver, credit note or refund took
 * off (negative for reversals). A percent rule takes its rate of the base; a fixed rule
 * takes its amount per unit in proportion to how much of the charge the base is.
 */
export function shareOf(
  rule: Pick<ShareRule, 'method' | 'rateBasisPoints' | 'fixedCents'>,
  charge: { totalCents: bigint; quantity: number },
  baseCents: bigint,
): bigint {
  if (rule.method === 'percent') {
    if (rule.rateBasisPoints === null) throw new RangeError('A percent rule needs a rate');
    return applyRate(moneyFromCents(baseCents), rule.rateBasisPoints).cents;
  }
  if (rule.fixedCents === null) throw new RangeError('A fixed rule needs an amount');
  if (charge.totalCents <= 0n) return 0n;
  const full = rule.fixedCents * BigInt(charge.quantity);
  return scaleMoney(moneyFromCents(full), baseCents, charge.totalCents).cents;
}

/**
 * Spreads an amount over weights without losing a cent (largest remainder, ADR 0006): a
 * refund over the invoice lines its payment paid, or an insurer's payment over a claim's
 * lines by their payer shares. Signed like the amount.
 */
export function spreadCents(cents: bigint, weights: readonly bigint[]): bigint[] {
  if (weights.length === 0 || weights.every((w) => w === 0n)) return weights.map(() => 0n);
  return allocateMoney(moneyFromCents(cents), weights).map((m) => m.cents);
}

/** The basis in effect on a day: the latest change on or before it; billed before any (R1). */
export function basisOn(
  changes: readonly { basis: RevenueShareBasis; effectiveFrom: CalendarDay }[],
  day: CalendarDay,
): RevenueShareBasis {
  let found: { basis: RevenueShareBasis; effectiveFrom: CalendarDay } | null = null;
  for (const c of changes)
    if (
      compareDays(c.effectiveFrom, day) <= 0 &&
      (found === null || compareDays(c.effectiveFrom, found.effectiveFrom) > 0)
    )
      found = c;
  return found?.basis ?? 'billed';
}

export interface StatementTotals {
  readonly earnedCents: bigint;
  readonly adjustmentsCents: bigint;
  readonly payableCents: bigint;
}

export function statementTotals(
  lines: readonly { kind: ShareEventKind; shareCents: bigint }[],
): StatementTotals {
  let earned = 0n;
  let adjustments = 0n;
  for (const l of lines) {
    if (isReversal(l.kind)) adjustments += l.shareCents;
    else earned += l.shareCents;
  }
  return { earnedCents: earned, adjustmentsCents: adjustments, payableCents: earned + adjustments };
}

export type StatementDecisionErrorCode =
  'revenue_share.approver_is_preparer' | 'revenue_share.approver_is_practitioner';

export const STATEMENT_DECISION_ERRORS: Record<StatementDecisionErrorCode, string> = {
  'revenue_share.approver_is_preparer':
    'You prepared this statement, so someone else with approval rights must decide it.',
  'revenue_share.approver_is_practitioner':
    'This is your own statement, so someone else with approval rights must decide it.',
};

/** R7: a second person decides, and never the practitioner the statement pays. */
export function checkStatementDecider(input: {
  preparedBy: string;
  practitionerUserId: string;
  deciderId: string;
}): Result<null, StatementDecisionErrorCode> {
  if (input.deciderId === input.preparedBy) return err('revenue_share.approver_is_preparer');
  if (input.deciderId === input.practitionerUserId)
    return err('revenue_share.approver_is_practitioner');
  return ok(null);
}

export type PayoutErrorCode =
  'payout.not_positive' | 'payout.nothing_payable' | 'payout.over_payable';

export const PAYOUT_ERRORS: Record<PayoutErrorCode, string> = {
  'payout.not_positive': 'A payout is more than zero.',
  'payout.nothing_payable':
    'Nothing is payable on this statement. A negative statement is settled outside Wodi.',
  'payout.over_payable': 'That is more than is left to pay on this statement.',
};

/** Payouts add up to at most the payable amount (R5: none on a statement with nothing payable). */
export function checkPayout(input: {
  payableCents: bigint;
  paidCents: bigint;
  amountCents: bigint;
}): Result<{ fullyPaid: boolean }, PayoutErrorCode> {
  if (input.amountCents <= 0n) return err('payout.not_positive');
  if (input.payableCents <= 0n) return err('payout.nothing_payable');
  const after = input.paidCents + input.amountCents;
  if (after > input.payableCents) return err('payout.over_payable');
  return ok({ fullyPaid: after === input.payableCents });
}
